import { css } from '@emotion/react';
import styled from '@emotion/styled';
import React, { FC, useState } from 'react';
import { createMarkup } from '@agria/utils/src/createMarkup';
import { ExtraInformation } from '@agria/theme/src/components/extraInformation/ExtraInformation';
import { Stack } from '../../stack/Stack';
import { Typography } from '../../typography/Typography';
import { Html } from '../../html/Html';
import { ArrowDown, Error, Info } from '../../icon';
import { Link } from '../../link/Link';
import { Surface } from '../../surface/Surface';
import { FormError } from '../formError/FormError';
import tokens from './tokens';

export interface FormFieldProps {
  label: string;
  name?: string;
  fieldAs?: 'div' | 'fieldset';
  hideLabel?: boolean;
  htmlFor?: string;
  invalid?: boolean;
  errorMessage?: string;
  errorMessageElementId?: string;
  hintText?: string | React.ReactNode;
  hintTextElementId?: string;
  disabled?: boolean;
  hidden?: boolean;
  optional?: boolean;
  extraInformation?: string | React.ReactNode;
  largeLabelGap?: boolean;
  small?: boolean;
  value?: string;
  expanderTitle?: string;
  expanderContent?: any;
  isOpen?: boolean;
  autocomplete?: string;
  required?: boolean;
}

type StyledFormFieldProps = Pick<FormFieldProps, 'disabled' & 'required'>;
type StyledFormFieldHeaderProps = Pick<
  FormFieldProps,
  'hideLabel' | 'largeLabelGap'
>;

interface StyledExpanderProps {
  isOpen: boolean;
}

const StyledFormField = styled.div<StyledFormFieldProps>`
  ${({ disabled, hidden }) => css`
    // Disabled

    ${disabled &&
    css`
      opacity: 0.6;
      cursor: not-allowed;
    `}

    // Hidden
    ${hidden &&
    css`
      display: none;
    `}
  `};
`;

const StyledFormFieldHeader = styled.div<StyledFormFieldHeaderProps>`
  ${({ theme: { space }, hideLabel, largeLabelGap }) => css`
    ${!hideLabel &&
    css`
      margin-bottom: ${largeLabelGap ? space.medium : space.xSmall};
    `}
  `};
`;

const StyledExpanderContent = styled(Surface)``;

const StyledExpander = styled(Stack)<StyledExpanderProps>`
  ${({ theme: { space }, isOpen }) => css`
    margin-top: ${space.fluidSmall};
    /* Animate content opening */
    ${StyledExpanderContent} {
      display: ${isOpen ? 'block' : 'none'};
    }
  `};
`;

export const FormField: FC<React.PropsWithChildren<FormFieldProps>> = ({
  label,
  fieldAs = 'div',
  hideLabel,
  htmlFor,
  children,
  invalid,
  errorMessage,
  errorMessageElementId,
  disabled,
  hintText,
  hintTextElementId,
  extraInformation,
  optional,
  hidden,
  largeLabelGap,
  small,
  value,
  expanderTitle,
  expanderContent,
  required,
  ...rest
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const handleClick = () => {
    setIsOpen(!isOpen);
  };
  return (
    <StyledFormField
      as={fieldAs}
      disabled={disabled}
      hidden={hidden}
      required={required}
      {...rest}
    >
      <StyledFormFieldHeader
        hideLabel={hideLabel}
        largeLabelGap={largeLabelGap}
        as={fieldAs === 'fieldset' ? 'legend' : 'div'}
      >
        <Stack gap="xSmall" as={fieldAs !== 'fieldset' ? 'label' : 'div'}>
          <Typography
            variant={small ? 'bodySmallBold' : 'bodyMediumBold'}
            htmlFor={htmlFor}
            visuallyHidden={hideLabel}
          >
            {label}
            {required && (
              <Typography
                component="span"
                variant={small ? 'bodySmallBold' : 'bodyMediumBold'}
                aria-label="required"
                title="required"
              >
                *
              </Typography>
            )}{' '}
            {optional && (
              <Typography
                component="span"
                variant={small ? 'bodySmallBold' : 'bodyMediumBold'}
              >
                (optional)
              </Typography>
            )}
          </Typography>
          {hintText &&
            (typeof hintText === 'string' ? (
              <Typography
                id={hintTextElementId || `${htmlFor || 'field'}-hint`}
                variant={small ? 'bodySmall' : 'bodyMedium'}
              >
                {createMarkup(hintText)}
              </Typography>
            ) : (
              hintText
            ))}
          {invalid && <FormError message={errorMessage || 'Required'} />}
          <FormError
            display={!!invalid && !!errorMessage}
            errorMessageElementId={errorMessageElementId}
            message={errorMessage || undefined}
          />
        </Stack>
      </StyledFormFieldHeader>

      {children}
      {expanderTitle && expanderContent && (
        <StyledExpander gap="small" isOpen={isOpen}>
          <Link
            type="button"
            onClick={handleClick}
            label={expanderTitle}
            icon={<ArrowDown />}
            darkIcon={tokens.darkIcon ?? false}
            aria-expanded={isOpen}
            circle
            rotateIcon={isOpen}
          />
          <StyledExpanderContent
            backgroundColor={tokens.expanderBackgroundColor}
            tab-index={!isOpen && -1}
            aria-hidden={!isOpen}
            padding="fluidSmall"
          >
            {typeof expanderContent === 'string' ? (
              <Html>{expanderContent}</Html>
            ) : (
              expanderContent
            )}
          </StyledExpanderContent>
        </StyledExpander>
      )}
      {extraInformation && <ExtraInformation text={extraInformation} />}
    </StyledFormField>
  );
};
